import { Injectable, EventEmitter, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subject, BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';

import { AuthService } from '../../core/services/auth/auth.service';

import { Monitor } from '../../core/models/entities/monitor.type';
import { Machine } from '../../core/models/entities/machine.type';
import { ScreenFrame } from '../../core/models/signalR-models/screen-frame';
import { Launcher } from '../../core/models/entities/launcher.type';
import { ErrorReport } from '../../core/models/view-models/error-report.model';
import { AppConfig } from '../../app.settings';
import { AppConstants } from '../../app.constants';
import { AsyncScheduleInfo } from '../../core/interfaces/async-schedule-info.interface';
import { PlaylistDTO } from '../../core/models/view-models/playlist-models/playlist-dto.model';
import { AsyncMachineInfo } from '../../core/interfaces/async-machine-info.interface';
import { Preset } from '../../wall-module/shared/models/preset.type';
import { IClientUpdateStatusPercentage } from '../../core/interfaces/client-update-status-percentage.interface';
import { IAdbNotifyUser } from '../../core/interfaces/adb/adb-notify-user.interface';
import { IAdbSpacedeskConfiguration } from '../../core/interfaces/adb/adb-spacedesk-configuration.interface';
import { IAdbIpAdress } from '../../core/interfaces/adb/adb-local-ip-adress.interface';
import { ADBConnectedDevicesModified } from '../../core/models/view-models/adb-models/adb-connected-devices-modified.view-model';
import { IIsDeviceCanBeDeleted } from '../../core/interfaces/adb/adb-is-device-can-be-deleted.interface';
import { IAdbUpdateSpacedeskConfiguration } from '../../core/interfaces/adb/adb-update-spacedesk-configuration.interface';
import { TagViewModel } from '../../core/models/view-models/tag-models/tag-view.model';
import { MachineCategory } from '../../core/models/entities/machine-category.type';
import { AuthDataInterface } from '../../core/models/view-models/account-models/auth-data.interface';
import { PresentationConvertionModel } from '../../core/models/entities/presentation-convertion.type';
import { Mp4ConvertionProgress } from '../../core/models/entities/mp4-to-webm-convertion-progress.type';
import { MonitorKey } from '../../core/models/view-models/monitor-models/monitor-key.type';
import { WindowPreviewInfo } from '../../core/models/entities/window-preview-info.model';

declare var $: any;

const enum UserInfoMessageType {
  Info,
  Success,
  Error
}

@Injectable()
export class MonitorHub {
  @Output() Connected: Subject<boolean> = new Subject<boolean>();
  @Output() Disconnected: Subject<void> = new Subject<void>();

  @Output() ScreenCacheChanged: EventEmitter<void> = new EventEmitter();
  @Output() ScreenCacheRemoved: EventEmitter<MonitorKey> = new EventEmitter();
  @Output() ScreenCacheAdded: EventEmitter<Monitor> = new EventEmitter();
  @Output() PlaylistUpdated: EventEmitter<PlaylistDTO> = new EventEmitter();
  @Output() PlaylistsUpdated: EventEmitter<PlaylistDTO[]> = new EventEmitter();
  @Output() PlaylistRemoved: EventEmitter<number> = new EventEmitter();
  @Output() TagsUpdated: EventEmitter<TagViewModel[]> = new EventEmitter();
  @Output() MonitorProfileUpdated: EventEmitter<Monitor> = new EventEmitter();
  @Output() MachineProfileUpdated: EventEmitter<Machine> = new EventEmitter();
  @Output() SchedulesUpdated: EventEmitter<void> = new EventEmitter();
  @Output() ReceiveServerUpdate: EventEmitter<ScreenFrame> = new EventEmitter();
  @Output() MachineCategoriesUpdated: EventEmitter<MachineCategory[]> = new EventEmitter();
  @Output() resolutionChanged = new EventEmitter<void>();

  @Output() ReceivedLogs: Subject<string> = new Subject();
  @Output() ReceiveRemoteControlStream: Subject<string> = new Subject();
  @Output() ReceiveRemoteControlScaling: BehaviorSubject<number> = new BehaviorSubject(1);
  @Output() WallsApplicationUpdated: Subject<string> = new Subject();
  @Output() WallsPresetUpdated: BehaviorSubject<Preset> = new BehaviorSubject(null);
  @Output() InfoNotification: BehaviorSubject<string> = new BehaviorSubject(null);
  @Output() IsClientRunningAsAdmin: Subject<boolean> = new Subject();
  @Output() IsOsbaseServiceRunning: Subject<boolean> = new Subject();
  @Output() IsAnyPlaylistRunning: Subject<boolean> = new Subject();
  @Output() IsClientRestartedAsAdmin: Subject<boolean> = new Subject();
  @Output() WallSuccessfullyApply: Subject<void> = new Subject();
  @Output() WallCleaning: Subject<void> = new Subject();

  @Output() AsyncMachineDesktopData: Subject<AsyncMachineInfo> = new Subject();
  @Output() AsyncScheduleDesktopData: Subject<AsyncScheduleInfo> = new Subject();
  @Output() pptFileConverted: Subject<PresentationConvertionModel> = new Subject();
  @Output() mp4ToWebmConvertionProgress: Subject<Mp4ConvertionProgress> = new Subject();
  @Output() PortraitToLandscapeConvertionProgress: Subject<Mp4ConvertionProgress> = new Subject();
  @Output() applyIsInPending: BehaviorSubject<boolean> = new BehaviorSubject(false);
  @Output() resetIsInPending: BehaviorSubject<boolean> = new BehaviorSubject(false);
  @Output() refreshIsInPending: BehaviorSubject<boolean> = new BehaviorSubject(false);
  @Output() isWallRefreshing: BehaviorSubject<boolean> = new BehaviorSubject(false);
  @Output() downloadLicenseKeysTriggered: Subject<string> = new Subject();
  @Output() clientUpdatingStatus: BehaviorSubject<IClientUpdateStatusPercentage> = new BehaviorSubject(null);
  @Output() windowsPreviewInfo: Subject<WindowPreviewInfo[]> = new Subject();

  LocalDataSubject = new BehaviorSubject<Launcher[]>([]);
  desktopMachineErrorOccured = new Subject<ErrorReport>();
  desktopMonitorErrorOccured = new Subject<ErrorReport>();

  AdbGetSpacedeskStatus: Subject<IAdbSpacedeskConfiguration> = new Subject();
  AdbConnectDevice: Subject<any> = new Subject();
  AdbGetConnectedDevices: Subject<ADBConnectedDevicesModified[]> = new Subject();
  AdbConnectedDevices: Subject<string[]> = new Subject();
  AdbUpdateDefaultSpacedeskConfiguration: Subject<IAdbUpdateSpacedeskConfiguration> = new Subject();
  ApkUpdated: Subject<string> = new Subject();
  AdbRestartSpacedesk: Subject<void> = new Subject();
  AdbDisconnectDevice: Subject<void> = new Subject();
  AdbUpdateSpacedesk: Subject<string> = new Subject();
  AdbGetLocalIpAddress: Subject<IAdbIpAdress> = new Subject();
  AdbEnableWirelessAccess: Subject<void> = new Subject();
  AdbErrorOccured: Subject<void> = new Subject();
  AdbServiceLoad: Subject<void> = new Subject();
  AdbUpdateSpacedeskConfigurationForAllDevices: Subject<any> = new Subject();
  AdbLocalMachineIpAddress: Subject<string> = new Subject();
  AdbCheckDeviceAvailable: Subject<IIsDeviceCanBeDeleted> = new Subject();

  deviceConnected: BehaviorSubject<string> = new BehaviorSubject(null);
  isNewDeviceAssigned: BehaviorSubject<boolean> = new BehaviorSubject(false);
  uniqueRegisterDeviceKeys: BehaviorSubject<Map<string, string>> = new BehaviorSubject(new Map());
  remoteControlDisconnected: Subject<string> = new Subject();

  public isConnected: boolean;
  public connection: SignalR.Hub.Connection;
  public proxy: SignalR.Hub.Proxy;
  public apiUrl: string;

  constructor(
    private readonly config: AppConfig,
    private readonly router: Router,
    private readonly toastrService: ToastrService,
    private readonly authService: AuthService
  ) {
    this.apiUrl = config.getConfig('host');
  }

  connectAsUser(): void {
    const authData = this.getToken();

    if (!authData || moment().isAfter(new Date(authData.expirationDate))) {
      this._navigateOnUnauthorized();
      return;
    }

    // initialize connection
    this.connection = $.hubConnection(
      this.config.getConfig('host') + 'signalr/'
    );
    this.connection.transportConnectTimeout = 3000;
    this.connection.qs = { access_token: authData.userToken };
    this.proxy = this.connection.createHubProxy('GatewayHub');

    this.proxy.on('DeviceConnected', (data: string) => {
      this.deviceConnected.next(data);
    });

    this.proxy.on('AdbConnectedDevices', (data: string[]) => {
      this.AdbConnectedDevices.next(data);
    });

    this.proxy.on('ApkUpdated', (data: string) => {
      this.ApkUpdated.next(data)
    });

    this.proxy.on('MachineProfileUpdated', (data) => {
      this.MachineProfileUpdated.emit(data);
    });

    this.proxy.on('PlaylistsUpdated', (data: PlaylistDTO[]) => {
      this.PlaylistsUpdated.emit(data);
    });

    this.proxy.on('PlaylistUpdated', (data: PlaylistDTO) => {
      this.PlaylistUpdated.emit(data);
    });

    this.proxy.on('PresentationFileConverted', (totalAmount: number, fileId: string) => {
      this.pptFileConverted.next(new PresentationConvertionModel(fileId, totalAmount));
    });

    this.proxy.on('Mp4ToWebmConvertionProgress', (percentage: number, fileName: string, fileId) => {
      this.mp4ToWebmConvertionProgress.next(new Mp4ConvertionProgress(percentage, fileId, fileName));
    });

    this.proxy.on('PortraitToLandscapeConvertionProgress', (percentage: number, fileId: string) => {
      this.PortraitToLandscapeConvertionProgress.next(new Mp4ConvertionProgress(percentage, fileId));
    });

    this.proxy.on('PlaylistRemoved', (id: number) => {
      this.PlaylistRemoved.emit(id);
    });

    this.proxy.on('MonitorTagsUpdated', (data: TagViewModel[]) => {
      this.TagsUpdated.emit(data);
    });

    this.proxy.on('ScreenCacheChanged', (data) => {
      this.ScreenCacheChanged.emit();
    });

    this.proxy.on('ScreenCacheAdded', (data: Monitor) => {
      this.ScreenCacheAdded.emit(data);
    });

    this.proxy.on('ScreenCacheRemoved', (data: string) => {
      const [physicalAddress, monitorHandler] = data.split(AppConstants.FULL_MONITOR_KEY_DELIMITER);
      this.ScreenCacheRemoved.emit(new MonitorKey(monitorHandler, physicalAddress));
    });

    this.proxy.on('LocalDataFetched', (data) => {
      this.LocalDataSubject.next(data);
    });

    this.proxy.on('ErrorOnDesktopOccured', (data) => {
      this.desktopMachineErrorOccured.next(data);
    });

    this.proxy.on('MachineDesktopData', (data: AsyncMachineInfo) => {
      this.AsyncMachineDesktopData.next(data);
    });

    this.proxy.on('ScheduleMachineData', (data: AsyncScheduleInfo) => {
      this.AsyncScheduleDesktopData.next(data);
    });

    this.proxy.on('TriggerEvaluationRestriction', () => {
      // Extra calls to the endpoint
      // this.subscription.add(this.licenseService.getLicenseStatus().subscribe());
    });

    this.proxy.on('SendLogsFile', (data: string) => {
      this.ReceivedLogs.next(data);
    });

    this.proxy.on('SendApplicationNameToFrontend', (data: string) => {
      this.WallsApplicationUpdated.next(data);
    });

    this.proxy.on('ReceiveRemoteControlStream', (data: string) => {
      this.ReceiveRemoteControlStream.next(data);
    });

    this.proxy.on('ReceiveRemoteControlScaling', (data: number) => {
      this.ReceiveRemoteControlScaling.next(data);
    });

    this.proxy.on('RemoteControlSessionDisconnected', (monitorHandler: string) => {
      this.remoteControlDisconnected.next(monitorHandler);
    });

    this.proxy.on('ResolutionChanged', () => {
      this.resolutionChanged.emit();
    });

    this.proxy.on('NotifyUserWithPreset', (presetData: Preset) => {
      this.WallsPresetUpdated.next(presetData);
    });

    this.proxy.on('InfoNotification', (textDescription: string) => {
      this.toastrService.success(textDescription);
      this.WallSuccessfullyApply.next();
    });

    this.proxy.on('NotifyIsClientRunningAsAdmin', (runningAsAdmin: boolean) => {
      this.IsClientRunningAsAdmin.next(runningAsAdmin);
    });

    this.proxy.on('NotifyOSBaseServiceIsNotRunning', (isRunning: boolean) => {
      this.IsOsbaseServiceRunning.next(isRunning);
    });

    this.proxy.on('NotifyIsClientReadyToUsage', (isPlaylistRunning: boolean) => {
      this.IsAnyPlaylistRunning.next(isPlaylistRunning);
    });

    this.proxy.on('NotifyIsClientRestarted', (isRestartAsAdmin: boolean) => {
      this.IsClientRestartedAsAdmin.next(isRestartAsAdmin);
    });

    this.proxy.on('NotifyUserWithLicenseKeysDownload', (data: string) => {
      this.downloadLicenseKeysTriggered.next(data);
    });

    this.proxy.on('NotifyWebClientWithUpdatePercentage', (data: IClientUpdateStatusPercentage) => {
      this.clientUpdatingStatus.next(data);
    });

    this.proxy.on('NotifyUserAdb', (data: IAdbNotifyUser) => {
      const sub$ = (this[data.Sender] as Subject<any>);
      if (sub$) {
        if (!data.Error) {
          sub$.next(data.Message);
        } else {
          this.AdbErrorOccured.next();
          sub$.error(data.Error);
        }
      }
    });

    this.proxy.on('NotifyUserWithServerInformation', (infoMessage: string, infoType: UserInfoMessageType = UserInfoMessageType.Info) => {
      switch (infoType) {
        case (UserInfoMessageType.Success):
          this.toastrService.success(infoMessage);
          break;
        case (UserInfoMessageType.Error):
          this.toastrService.error(infoMessage);
          break;
        default:
          this.toastrService.info(infoMessage);
      }
    });

    this.proxy.on('DeviceCategoriesUpdated', (data: MachineCategory[]) => {
      this.MachineCategoriesUpdated.emit(data);
    });

    this.proxy.on('UpdateToken', () => {
      const authData = this.getToken();
      if (authData) {
        this.updateToken(authData.refreshToken);
      }
    });

    this.proxy.on('WindowsPreviewInfo', (data: WindowPreviewInfo[]) => {
      this.windowsPreviewInfo.next(data);
    });

    this.connection
      .start({
        withCredentials: false,
        jsonp: true,
        transport: 'webSockets',
      })
      .done(() => {
        this.Connected.next(true);
        this.isConnected = true;
        this.proxy
          .invoke('registerToWebUserGroup')
          .fail((err: HttpErrorResponse) => {
            // console.log('Failed registerToWebUserGroup');
            // console.log(err);
            if (err.status === 401) {
              this._navigateOnUnauthorized();
            }
          });
      })
      .fail((err: HttpErrorResponse) => {
        // console.log('Failed user connection');
        // console.log(err);
        if (err.status === 401) {
          this._navigateOnUnauthorized();
        }
        this.isConnected = false;
        this.Connected.next(false);
      });

    this.connection.reconnecting(() => {
      const authData = this.getToken();

      if (authData && moment().isAfter(new Date(authData.expirationDate))) {
        this.connection.stop();
        this.updateToken(authData.refreshToken, true);
      }
    });

    this.connection.disconnected(() => {
      this.Disconnected.next();
    });
  }

  connectAsMonitor(handler: string, mac: string, allowUnauthorized: boolean = false): void {
    const authData = this.getToken();

    if (!authData && !allowUnauthorized) {
      this._navigateOnUnauthorized();
      return;
    }

    // initialize connection
    this.connection = $.hubConnection(
      this.config.getConfig('host') + 'signalr/'
    );
    this.connection.transportConnectTimeout = 120000;

    if (!allowUnauthorized) {
      this.connection.qs = { access_token: authData.userToken };
    }

    this.proxy = this.connection.createHubProxy('GatewayHub');

    this.proxy.on('GetServerUpdate', (data) => {
      this.ReceiveServerUpdate.emit(data);
    });

    this.proxy.on('MonitorProfileUpdated', (monitor) => {
      this.MonitorProfileUpdated.emit(monitor);
    });

    this.proxy.on('SchedulesUpdated', (data) => {
      this.SchedulesUpdated.emit();
    });

    this.proxy.on('ErrorOnDesktopOccured', (data) => {
      this.desktopMonitorErrorOccured.next(data);
    });

    this.connection
      .start({
        withCredentials: false,
        jsonp: true,
        transport: 'webSockets',
      })
      .done(() => {
        this.Connected.next(true);
        this.isConnected = true;
        this.proxy
          .invoke('registerToWebMonitorGroup', handler, mac)
          .fail((err: HttpErrorResponse) => {
            // console.log('Failed user registerToWebMonitorGroup');
            // console.log(err);
            if (err.status === 401) {
              this._navigateOnUnauthorized();
            }
          });
      })
      .fail((err: HttpErrorResponse) => {
        // console.log('Failed user connection');
        // console.log(err);
        if (err.status === 401) {
          this._navigateOnUnauthorized();
        }
        this.Connected.next(false);
        this.isConnected = false;
      });
  }

  public triggerFetchingLocalData(physicalAddress: string) {
    this.LocalDataSubject.next([]);
    if (!physicalAddress) {
      return;
    }
    this.proxy
      .invoke('TriggerFetchingLocalData', physicalAddress)
      .fail((err: HttpErrorResponse) => {
        if (err.status === 401) {
          this._navigateOnUnauthorized();
        } else {
          throw err;
        }
      });
  }

  invokeFetchingClientMachineData() {
    this.proxy
      .invoke('TriggerFetchingMachineData')
      .fail((err) => console.log('TriggerFetchingMachineData was failed', err));
  }

  invokeFetchingScheduleData(physicalAddress: string) {
    this.proxy
      .invoke('TriggerScheduleData', physicalAddress)
      .fail((err) =>
        console.log('TriggerFetchingScheduleData was failed', err)
      );
  }

  invokeCloseFullscreenView(physicalAddress: string, handler: string) {
    try {
      this.proxy.invoke('TriggerCloseFullscreenView', physicalAddress, handler);
    } catch (err) {
      console.log('TriggerCloseFullscreenView was failed', err);
    }
  }

  public disconnect() {
    if (this.connection) {
      this.connection.stop(false, true);
      this.connection = null;
    }
  }

  _navigateOnUnauthorized() {
    this.router.navigateByUrl('/Account');
  }

  private getToken(): AuthDataInterface {
    return JSON.parse(
      localStorage.getItem(AppConstants.AUTH_DATA_NAME)
    );
  }

  private updateToken(token: string, isToReconnectAsUser: boolean = false): void {
    this.authService.refreshToken(token)
      .pipe(take(1))
      .subscribe(() => {
        if (isToReconnectAsUser) {
          this.connectAsUser();
        }
      });
  }
}
